import { useMemo } from "react";
import type { GroupProps, ValidationFields } from "../ComparativeAnalysisForm.types";

export const useIsValidFormFields = (
  baseFieldsValidation: ValidationFields,
  compareFieldsValidation: ValidationFields,
  group1: GroupProps,
  group2: GroupProps
) => {
  const hasValidFormFields = useMemo(() => {
    // Create an array of field-validation pairs to check
    const validationPairs = [
      // base fields
      [group1.env, baseFieldsValidation.env[0]],
      [group1.pipeline, baseFieldsValidation.pipeline[0]],
      [group1.pit, baseFieldsValidation.pit[0]],
      [group1.task, baseFieldsValidation.task[0]],
      // compare fields
      [group2.env, compareFieldsValidation.env[0]],
      [group2.pipeline, compareFieldsValidation.pipeline[0]],
      [group2.pit, compareFieldsValidation.pit[0]],
      [group2.task, compareFieldsValidation.task[0]],
    ];

    // If any field with a value has a false validation, return false
    return !validationPairs.some(
      ([value, validation]) => value && validation === false
    );
  }, [baseFieldsValidation, compareFieldsValidation, group1, group2]);

  // Check if all required fields are empty
  const allFieldsEmpty = useMemo(() => {
    return (
      !group1.env &&
      !group1.pipeline &&
      !group1.pit &&
      !group2.env &&
      !group2.pipeline &&
      !group2.pit
    );
  }, [group1, group2]);

  const requiredFieldsEmpty = useMemo(() => {
    const required =
      !group1.env ||
      !group1.pipeline ||
      !group1.pit ||
      !group2.env ||
      !group2.pipeline ||
      !group2.pit;

    // If either task has a value, both must have values
    if (group1.task || group2.task) {
      return required || !group1.task || !group2.task;
    }

    return required;
  }, [group1, group2]);

  const identicalGroupFields = useMemo(() => {
    const sameEnv = group1.env === group2.env;
    const samePipeline = group1.pipeline === group2.pipeline;
    const samePit = group1.pit === group2.pit;

    if (hasValidFormFields && !requiredFieldsEmpty) {
      if (!sameEnv || !samePipeline || !samePit) {
        return false;
      }

      if (samePit && (group1.task || group2.task)) {
        return group1.task === group2.task;
      }

      return true;
    }

    return false;
  }, [hasValidFormFields, requiredFieldsEmpty, group1, group2]);

  return {
    allFieldsEmpty,
    requiredFieldsEmpty,
    hasValidFormFields,
    identicalGroupFields,
  };
};
